import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { LocaleService } from '@next/core-lib/i18n';
import { AgrSharedModule } from '@next/core-lib/shared';
import { TableColumnTypes, TableConfig, AgrTableModule, TableColumn } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SiteState } from '../../../shared/models/models';
import { MeasurementSummary, Type } from '../../models/measurements.model';
import * as MeasurementsActions from '../../store/measurements.actions';
import { getMeasurementOverview } from '../../store/measurements.selectors';

interface MeasurementOverviewRow {
  id: number;
  name: string;
  type: Type;
  creationDate: string;
  dmPercentage: number;
  ndf: number;
  stickiness: number;
  averageParticleLength: number;
  peNDF8: number;
  'peNDF1.18': number;
  hoursAfterFeeding: string;
}

@Component({
  selector: 'app-measurements-overview',
  templateUrl: './measurements-overview.component.html',
  imports: [CommonModule, AgrSharedModule, AgrTableModule],
})
export class MeasurementsOverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly localeService = inject(LocaleService);

  @ViewChild('typeColumn') typeColumn: TemplateRef<unknown>;
  @ViewChild('rationTypeColumn') rationTypeColumn: TemplateRef<unknown>;
  @ViewChild('speciesColumn') speciesColumn: TemplateRef<unknown>;

  readonly type = Type;
  tableConfig: TableConfig = {
    columns: [],
    dataSource: new MatTableDataSource([]),
    header: true,
    footer: false,
    scrolling: true,
    alternateRows: true,
    dragDrop: false,
    rowClickAction: {
      id: 'view',
      callback: this.openExistingMeasurement.bind(this),
    },
    actionColumn: true,
    actions: [
      {
        id: 'edit',
        label: this.localeService.translate('measurements.update'),
        icon: 'edit',
        callback: this.openExistingMeasurement.bind(this),
        disabled: () => false,
      },
      {
        id: 'copy',
        label: this.localeService.translate('measurements.copy'),
        icon: 'content_copy',
        callback: this.copyMeasurement.bind(this),
        disabled: this.checkDisabled,
      },
      {
        id: 'delete',
        label: this.localeService.translate('measurements.delete'),
        icon: 'delete',
        callback: this.deleteMeasurement.bind(this),
        disabled: () => false,
      },
    ],
    selectColumn: false,
    pagination: true,
    primaryColumn: 'name',
    activeSort: {
      id: 'creationDate',
      start: 'desc',
      disableClear: false,
    },
  };

  private readonly measurementOverview$ = this.store.select(getMeasurementOverview);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.measurementOverview$.pipe(takeUntil(this.destroy$)).subscribe((measurementOverview) => {
      this.tableConfig = {
        ...this.tableConfig,
        dataSource: new MatTableDataSource(this.createDataSource(measurementOverview)),
        columns: this.createColumns(),
      };
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createDataSource(measurementOverview: MeasurementSummary[]): MeasurementOverviewRow[] {
    return (measurementOverview || [])
      .map((measurementSummary) => ({
        id: measurementSummary.id,
        name: measurementSummary.name,
        type: measurementSummary.type,
        rationType: measurementSummary.rationType,
        species: measurementSummary.species,
        creationDate: measurementSummary.created,
        dmPercentage: Number.isNaN(measurementSummary.dmPercentage)
          ? null
          : measurementSummary.dmPercentage,
        ndf: typeof measurementSummary.ndf === 'number' ? measurementSummary.ndf : null,
        stickiness:
          typeof measurementSummary.stickiness === 'number' ? measurementSummary.stickiness : null,
        averageParticleLength:
          typeof measurementSummary.averageParticleLength === 'number'
            ? measurementSummary.averageParticleLength
            : null,
        peNDF8: typeof measurementSummary.peNDF8 === 'number' ? measurementSummary.peNDF8 : null,
        'peNDF1.18':
          typeof measurementSummary['peNDF1.18'] === 'number'
            ? measurementSummary['peNDF1.18']
            : null,
        hoursAfterFeeding: measurementSummary.hoursAfterFeeding,
      }))
      .sort(
        (row1, row2) =>
          new Date(row2.creationDate).getTime() - new Date(row1.creationDate).getTime(),
      );
  }

  private createColumns(): TableColumn[] {
    return [
      {
        id: 'creationDate',
        label: 'measurements.overview.creation-date',
        type: TableColumnTypes.DATE,
        headerClassName: 'flex__child--2',
        className: 'flex__child--2',
        footerClassName: 'flex__child--2',
      },
      {
        id: 'hoursAfterFeeding',
        label: 'measurements.overview.hoursAfterFeeding',
        type: TableColumnTypes.STRING,
        headerClassName: 'flex__child--1',
        className: 'flex__child--1',
        footerClassName: 'flex__child--1',
      },
      {
        id: 'name',
        label: 'measurements.overview.name',
        type: TableColumnTypes.STRING,
        headerClassName: 'flex__child--2',
        className: 'flex__child--2',
        footerClassName: 'flex__child--2',
      },
      {
        id: 'type',
        label: 'measurements.overview.type',
        type: TableColumnTypes.TEMPLATE,
        template: this.typeColumn,
      },
      {
        id: 'rationType',
        label: 'measurements.overview.ration-type',
        type: TableColumnTypes.TEMPLATE,
        template: this.rationTypeColumn,
      },
      {
        id: 'species',
        label: 'measurements.overview.animal-group',
        type: TableColumnTypes.TEMPLATE,
        template: this.speciesColumn,
      },
      {
        id: 'dmPercentage',
        label: 'measurements.overview.dry-matter-percentage',
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-1',
      },
      {
        id: 'ndf',
        label: 'measurements.overview.ndf-value',
        type: TableColumnTypes.NUMBER,
        digitsInfo: '1.0-3',
      },
    ];
  }

  private openExistingMeasurement({ id }: MeasurementOverviewRow): void {
    this.store.dispatch(
      MeasurementsActions.loadMeasurementRequest({
        measurementId: id,
      }),
    );
  }

  private copyMeasurement(row: MeasurementOverviewRow): void {
    this.store.dispatch(
      MeasurementsActions.copyMeasurementRequest({
        existingMeasurementSummaryId: row.id,
      }),
    );
  }

  private deleteMeasurement({ id }: MeasurementOverviewRow): void {
    this.store.dispatch(MeasurementsActions.deleteMeasurementRequest({ measurementId: id }));
  }

  private checkDisabled(row: MeasurementOverviewRow): boolean {
    return row.type !== Type.Ration;
  }
}
